/**
 * Dependencies
 */
import http from '../../../services/HttpService'

/**
 * Reset the state
 *
 * @param  {Function} options.commit
 * @return {Void}
 */
const resetState = ({ commit }) => commit('RESET_STATE')

/**
 * Change the selected step in the importer process
 *
 * @param  {Function} options.commit
 * @param  {Number} step
 * @return {Promise}
 */
const changeSelectedStep = ({ commit }, step) => commit('CHANGE_STEP', step)

/**
 * Upload a rent roll or P&L file
 *
 * @param  {Function} options.commit
 * @param  {Object} payload
 * @return {Void}
 */
const uploadFile = ({ commit }, payload) => {
    commit('SET_UPLOADING')
    http.post(`data-import/import-files/${payload.type}/`, payload.data)
        .then(res => {
            commit('SET_CONTEXT', payload.type)
            commit('SET_FILE_DATA', { type: payload.type, data: res.data })
            commit('UPLOAD_SUCCESS')
            commit('RESET_UPLOADING')
        })
        .catch(err => {
            commit('SET_UPLOAD_ERROR', err.response.data)
            commit('UPLOAD_FAILED')
            commit('RESET_UPLOADING')
        })
}

/**
 * Allow the user to request assistance with a file that fails to upload properly
 *
 * @param  {Function} options.commit
 * @param  {Number} id
 * @return {Promise}
 */
const requestFileAssistance = ({ commit, state }) => {
    const fileType = state.upload.error.rentroll_file_id
        ? 'rentroll_file_id'
        : 'opex_file_id'
    commit('REQUEST_FILE_ASSISTANCE')
    http.post(`projects/import-file/${state.upload.error[fileType]}/support/`)
        .then(res => commit('SUPPORT_REQUEST_SUCCESS', res))
        .catch(err => commit('SUPPORT_REQUEST_FAILURE', err))
}

/**
 * Get the data analysis for an file.
 *
 * @param  {Function} options.commit
 * @param  {Object} options.state
 * @return {Void}
 */
const getAnalysis = ({ commit, state }) => new Promise((resolve, reject) => {
    commit('SET_ANALYSIS_LOADING')
    http.get(`data-import/import-files/${state.data[state.context].data.meta[state.context + '_file_id']}/analysis/`)
        .then(({ data }) => {
            data.opex.general[0].type = 'opex'
            data.opex.general[0].name = 'Operating Expenses'
            data.miscinc.general[0].type = 'miscinc'
            data.miscinc.general[0].name = 'Miscellaneous Income'
            commit('SET_ANALYSIS_DATA', data)
            commit('RESET_ANALYSIS_LOADING')
            resolve()
        })
        .catch(err => {
            commit('SET_ANALYSIS_ERROR')
            commit('RESET_ANALYSIS_LOADING')
            reject(err)
        })
})

/**
 * Update the filter string
 *
 * @param  {Function} options.commit
 * @param  {String} value
 * @return {Promise}
 */
const updateFilterValue = ({ commit }, value) => commit('UPDATE_FILTER_VALUE', value)

/**
 * Update the actively viewed item
 *
 * @param  {Function} options.commit
 * @param  {Object} payload
 * @return {Promise}
 */
const changeActiveItem = ({ commit }, payload) => {
    console.log(payload)
    commit('CHANGE_ACTIVE_ITEM', payload)
}

/**
 * Update the selected timeframe for a line item
 *
 * @param  {Function} options.commit
 * @param  {Object} options.state
 * @param  {String} timeframe
 * @return {Void}
 */
const updateTimeframe = ({ commit, state, getters, dispatch }, timeframe) => new Promise((resolve, reject) => {
    const url = `data-import/import-files/${state.data[state.context].data.meta[state.context + '_file_id']}/analysis/`
    const data = getters.sidebarListItems[state.analysis.selectedItem.type][state.analysis.selectedItem.category].items
        .filter(i => {
            if (state.analysis.selectedItem.index === null) return true
            return i.index === state.analysis.selectedItem.index
        })
        .map(i => { return { index: i.index, time_frame: timeframe } })
    if (state.analysis.selectedItem.index !== null) { commit('SET_CURRENT_TIMEFRAME', timeframe) }
    http.patch(url, { time_frames: data })
        .then(res => {
            dispatch('getAnalysis')
            resolve()
        })
        .catch(err => reject(err))
})

/**
 * Adjust an item's value
 *
 * @param  {Function} options.commit
 * @param  {Object} options.state
 * @param  {Object} payload
 * @return {Promise}
 */
const adjustValue = ({ commit, state }, payload) => new Promise((resolve, reject) => {
    const url = `data-import/import-files/${state.data[state.context].data.meta[state.context + '_file_id']}/analysis/`
    http.patch(url, {
        values: [{
            index: state.analysis.selectedItem.index,
            data: {
                index: state.analysis.data.meta.date_indexes[payload.dataIndex],
                value: payload.value
            }
        }]
    })
    .then(res => {
        commit('UPDATE_LINE_ITEM_VALUE', {
            targetIndex: res.data.data[0].index,
            dataIndex: payload.dataIndex,
            value: payload.value
        })
        commit('UPDATE_ANNUALIZED_AMOUNTS', res.data.data[0])
        resolve()
    })
    .catch(err => reject(err))
})

/**
 * Process the import
 *
 * @param  {Function} options.commit
 * @return {Promise}
 */
const processImport = ({ commit }) => new Promise((resolve, reject) => {
    setTimeout(() => {
        commit('SET_IMPORT_SUCCESS')
        resolve()
    }, 1500)
})

/**
 * Reset the analysis state
 *
 * @param  {Function} options.commit
 * @return {Promise}
 */
const resetAnalysis = ({ commit }) => commit('RESET_ANALYSIS_STATE')

/**
 * Toggle showing the the input/output bindings
 *
 * @param  {Function} options.commit
 * @return {Promise}
 */
const toggleInputBindings = ({ commit }) => commit('TOGGLE_INPUT_BINDINGS')

/**
 * Update the category for an existing output item
 *
 * @param  {Object} options.state
 * @param  {Object} payload
 * @return {Void}
 */
const updateCategory = ({ commit, state, dispatch }, payload) => {
    http.patch(`data-import/import-files/${state.data[state.context].data.meta[state.context + '_file_id']}/update/`, payload)
        .then(res => {
            commit('SET_FILE_DATA', { type: state.context, data: res.data })
            dispatch('addNotification', {
                title: 'Item Reassigned',
                type: 'success',
                text: 'The line item was successfully recategorized',
                timeout: true
            }, { root: true })
        })
        .catch(e => {
            dispatch('addNotification', {
                title: 'Error',
                type: 'error',
                text: 'There was a problem uploading your file.',
                timeout: true
            }, { root: true })
        })
}

/**
 * Next page
 *
 * @param  {Function} options.commit
 * @return {Promise}
 */
const nextPage = ({ commit }) => commit('PAGINATE_FORWARD')

/**
 * Previous Page
 *
 * @param  {Function} options.commit
 * @return {Promise}
 */
const prevPage = ({ commit }) => commit('PAGINATE_BACKWARD')

/**
 * First Page
 *
 * @param  {Function} options.commit
 * @return {Promise}
 */
const firstPage = ({ commit }) => commit('PAGINATE_FIRST')

/**
 * Last Page
 *
 * @param  {Function} options.commit
 * @return {Promise}
 */
const lastPage = ({ commit }) => commit('PAGINATE_LAST')

/**
 * Execute call to update a user's file mapping
 *
 * @param  {Function} options.commit
 * @return {Promise}
 */
const updateMapping = ({ commit, state, dispatch }, payload) => {
    const url = `data-import/import-files/${state.data.rentroll.data.meta.rentroll_file_id}/update/`

    commit('SET_UPDATE_MAPPING')
    http.patch(url, { mapping: payload })
        .then(res => {
            setTimeout(() => {
                commit('LOAD_NEW_MAPPING', res.data)
                commit('RESET_UPDATE_MAPPING')
                dispatch('addNotification', {
                    type: 'success',
                    title: 'Mapping Updated',
                    text: 'The file mappings have been updated successfully.',
                    timeout: true
                }, { root: true })
            }, 1500)
        })
        .catch(err => {
            setTimeout(() => {
                commit('RESET_UPDATE_MAPPING', err)
            }, 1500)
        })
}

/**
 * Update the selected tab
 *
 * @param  {Function} options.commit
 * @param  {Number} tab
 * @return {Promise}
 */
const changeTab = ({ commit }, tab) => commit('CHANGE_TAB', tab)

/**
 * Import the data into a project
 *
 * @param  {Object} options.state
 * @param  {Function} options.dispatch
 * @return {Promise}
 */
const importPnlData = ({ state, dispatch, commit }) => new Promise((resolve, reject) => {
    commit('SET_IMPORTING')
    http.get(`data-import/import-files/${state.data[state.context].data.meta[state.context + '_file_id']}/import/`)
    .then(res => {
        dispatch('projects/form/ingestOpexValues', res.data, {
            root: true
        })
        commit('RESET_IMPORTING')
        commit('IMPORT_SUCCESSFUL')
        resolve(res)
    })
    .catch(err => {
        dispatch('addNotification', {
            type: 'error',
            title: 'Import failed',
            text: err.response.data,
            timeout: true
        }, { root: true })
        commit('RESET_IMPORTING')
        reject(err)
    })
})

/**
 * Update the mappings and get a new rendered dataset
 *
 * @param  {Function} options.commit
 * @return {Void}
 */
const importRentRollData = ({ commit, state, dispatch }) => new Promise((resolve, reject) => {
    commit('SET_IMPORTING')
    http.get(`data-import/import-files/${state.data[state.context].data.meta[state.context + '_file_id']}/import/`)
        .then(res => {
            commit('projects/form/IMPORT_UNIT_VALUES', res.data, { root: true })
            commit('projects/form/UPDATE_FILE_ID', {
                id: state.data[state.context].data.meta[state.context + '_file_id'],
                type: 'rentroll_file_id'
            }, { root: true })
            dispatch('addNotification', {
                type: 'success',
                title: 'Rent Roll Imported',
                text: 'Rent Roll was successfully imported to your project.',
                timeout: true
            }, { root: true })
            commit('RESET_IMPORTING')
            commit('IMPORT_SUCCESSFUL')
            resolve()
        })
        .catch(err => {
            dispatch('addNotification', {
                type: 'error',
                title: 'Import Failed',
                text: 'Rent Roll was not successfully imported.',
                timeout: true
            }, { root: true })
            commit('RESET_IMPORTING')
            reject(err)
        })
})

export default {
    nextPage,
    prevPage,
    lastPage,
    changeTab,
    firstPage,
    resetState,
    uploadFile,
    getAnalysis,
    adjustValue,
    updateMapping,
    resetAnalysis,
    importPnlData,
    processImport,
    updateCategory,
    updateTimeframe,
    changeActiveItem,
    updateFilterValue,
    importRentRollData,
    changeSelectedStep,
    toggleInputBindings,
    requestFileAssistance
}



// WEBPACK FOOTER //
// ./src/state/modules/importer/actions.js